/**
 * High-Performance Client-Side API Cache
 * - In-memory Map with automatic TTL expiry
 * - In-flight request deduplication (prevents redundant parallel HTTP roundtrips)
 * - Transparent fallback on network failure
 */

import { getClientSecurityHeaders } from './security';

const cacheStore = new Map();
const inflightRequests = new Map();
const DEFAULT_TTL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Fetch with in-memory caching, request deduplication, and anti-scraping verification
 * @param {string} url - Target API URL
 * @param {RequestInit} [options] - Standard fetch options
 * @param {number} [ttlMs] - Time-to-live in milliseconds
 * @returns {Promise<any>}
 */
export async function fetchWithCache(url, options = {}, ttlMs = DEFAULT_TTL_MS) {
  const method = (options.method || 'GET').toUpperCase();
  const { signal: callerSignal, ...fetchOptions } = options;
  
  // Attach dynamic browser security verification headers binding request attributes
  let mergedOptions = fetchOptions;
  try {
    const secHeaders = await getClientSecurityHeaders(method, url, fetchOptions.body);
    mergedOptions = {
      ...fetchOptions,
      headers: {
        ...secHeaders,
        ...(fetchOptions.headers || {})
      }
    };
  } catch (e) {
    // Graceful fallback
  }

  // Only cache idempotent GET requests
  if (method !== 'GET') {
    const res = await fetch(url, mergedOptions);
    if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
    return res.json();
  }

  const cacheKey = url;
  const now = Date.now();

  // 1. Check in-memory cache
  const cached = cacheStore.get(cacheKey);
  if (cached && now - cached.timestamp < ttlMs) {
    return cached.data;
  }

  // 2. Check if caller is already aborted
  if (callerSignal?.aborted) {
    throw new DOMException('The user aborted a request.', 'AbortError');
  }

  // 3. Check if identical request is already in-flight or initiate network request
  let fetchPromise = inflightRequests.get(cacheKey);
  if (!fetchPromise) {
    fetchPromise = (async () => {
      try {
        const res = await fetch(url, mergedOptions);
        if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
        const data = await res.json();
        
        cacheStore.set(cacheKey, {
          data,
          timestamp: Date.now()
        });

        return data;
      } finally {
        inflightRequests.delete(cacheKey);
      }
    })();
    inflightRequests.set(cacheKey, fetchPromise);
  }

  // 4. Return promise, hooking caller signal if provided so only this caller aborts
  if (callerSignal) {
    return new Promise((resolve, reject) => {
      const abortHandler = () => {
        reject(new DOMException('The user aborted a request.', 'AbortError'));
      };
      callerSignal.addEventListener('abort', abortHandler, { once: true });
      fetchPromise.then(
        (data) => {
          callerSignal.removeEventListener('abort', abortHandler);
          resolve(data);
        },
        (err) => {
          callerSignal.removeEventListener('abort', abortHandler);
          reject(err);
        }
      );
    });
  }

  return fetchPromise;
}

/**
 * Clear cached data for specific key or prefix pattern
 * @param {string} [pattern]
 */
export function clearApiCache(pattern) {
  if (!pattern) {
    cacheStore.clear();
    return;
  }
  for (const key of cacheStore.keys()) {
    if (key.includes(pattern)) {
      cacheStore.delete(key);
    }
  }
}
